/**
 * Session-start notifications (booked starting-soon + Start now).
 * Payloads never include location, address, phone, email, or IP.
 */

import {
  isStartNowBooking,
  sessionFormatDisplayName,
  sessionFormatFromTransaction,
} from './sessionPricing';
import { hasRememberedSessionEntry } from './sessionMediaClient';
import { bookingTimes, txPastNoShowDeadline } from './sessionClock';
import { txIsListenerNoShow } from './sessionNoShowCountdown';
import { transactionHidesJoin } from './moderationBooking';
import { isSupersededEditedBooking } from './editUpcomingBooking';

export const STARTING_SOON_MS = 5 * 60 * 1000;
export const SESSION_NOTIFICATION_POLL_MS = 60000;
export const BROWSER_NOTIF_STORAGE_PREFIX = 'sl-session-notif:';
// Deep-link hash only. Access is party-auth on /order|/sale/{transactionId},
// which is unique per booking and unusable by anyone else.
export const JOIN_SESSION_HASH = 'session-join';
export const JOIN_SESSION_ENTER_PARAM = 'enter';

/**
 * True when a Join deep link should open the live session, not just scroll.
 *
 * @param {string|Object} [search]
 * @returns {boolean}
 */
export const shouldAutoEnterSession = search => {
  const value =
    typeof search === 'string'
      ? search
          .replace(/^\?/, '')
          .split('&')
          .map(part => part.split('='))
          .find(([key]) => key === JOIN_SESSION_ENTER_PARAM)?.[1]
      : search?.[JOIN_SESSION_ENTER_PARAM];
  return value === '1' || value === 1 || value === true || value === 'true';
};

/**
 * True when the URL hash is the booking Join target.
 *
 * @param {string} [hash]
 * @returns {boolean}
 */
export const isJoinSessionHash = hash => {
  if (typeof hash !== 'string' || !hash) {
    return false;
  }
  return hash === JOIN_SESSION_HASH || hash === `#${JOIN_SESSION_HASH}`;
};

/**
 * First Join (Dashboard, bell, banner, popup) should enter the room.
 *
 * @param {{ search?: string|Object, hash?: string }} [location]
 * @returns {boolean}
 */
export const shouldAttemptSessionEnter = ({ search, hash } = {}) =>
  shouldAutoEnterSession(search) || isJoinSessionHash(hash);

/**
 * NamedLink `to` for a one-click Join into the live session.
 *
 * @returns {{ search: string, hash: string }}
 */
export const joinSessionLinkTo = () => ({
  search: `?${JOIN_SESSION_ENTER_PARAM}=1`,
  hash: `#${JOIN_SESSION_HASH}`,
});

/**
 * End call / hang-up is not the end of the booking. The room stays
 * joinable until the booked clock (or no-show / policy) expires.
 *
 * @param {Object} [session]
 * @returns {boolean}
 */
export const isSoftLeaveEnd = session =>
  !!session?.ended && (session.endedReason === 'left' || !session.endedReason);

/**
 * True when Join must stay hidden and the page should say the session ended.
 *
 * @param {Object} [session]
 * @returns {boolean}
 */
export const isHardSessionEnd = session => !!session?.ended && !isSoftLeaveEnd(session);

export const JOINABLE_LAST_TRANSITIONS = ['transition/accept', 'transition/operator-accept'];

export const TERMINAL_JOIN_TRANSITIONS = [
  'transition/cancel',
  'transition/customer-cancel',
  'transition/provider-cancel',
  'transition/expire-accepted',
  'transition/operator-cancel-separate-hold',
  'transition/decline',
  'transition/operator-decline',
  'transition/customer-cancel-request',
  'transition/expire',
  'transition/expire-payment',
  'transition/complete',
  'transition/operator-complete',
  'transition/operator-complete-separate-hold',
  'transition/charge-after-session',
  'transition/provider-charge-after-session',
  'transition/review-1-by-customer',
  'transition/review-2-by-customer',
  'transition/review-1-by-provider',
  'transition/review-2-by-provider',
  'transition/review',
  'transition/expire-review-period',
  'transition/expire-customer-review-period',
  'transition/expire-provider-review-period',
];

export const NOTIFICATION_PUBLIC_KEYS = [
  'kind',
  'id',
  'listingId',
  'listingTitle',
  'sessionFormat',
  'startsAt',
  'endsAt',
  'role',
  'otherDisplayName',
  'joinName',
  'joinParams',
  'lastTransition',
];

const FORBIDDEN_KEY_RE = /^(phone|email|address|location|geolocation|neighborhood|origin|ipAddress|ip)$/i;
const PHONE_RE = /(?:\+\d{1,3}[\s.-]?)?(?:\(?\d{3}\)?[\s.-]?)\d{3}[\s.-]?\d{4}/;
const EMAIL_RE = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i;
const formatLabel = sessionFormat => sessionFormatDisplayName(sessionFormat);

const joinPhrase = sessionFormat => {
  if (sessionFormat === 'voice') {
    return 'join Voice call';
  }
  if (sessionFormat === 'video') {
    return 'join Video call';
  }
  if (sessionFormat === 'chat') {
    return 'join Text chat';
  }
  return 'join the session';
};

export const isJoinableLastTransition = lastTransition =>
  JOINABLE_LAST_TRANSITIONS.includes(lastTransition);

export const isTerminalJoinTransition = lastTransition =>
  TERMINAL_JOIN_TRANSITIONS.includes(lastTransition);

const protectedDataOf = tx => tx?.attributes?.protectedData || {};

/**
 * True when a talk session was canceled for a listener no-show.
 *
 * @param {Object} [tx]
 * @returns {boolean}
 */
export const transactionIsListenerNoShow = tx => txIsListenerNoShow(tx);

/**
 * True when a talk session ended because the talker never joined.
 *
 * @param {Object} [tx]
 * @returns {boolean}
 */
export const transactionIsTalkerNoShow = tx => {
  const data = protectedDataOf(tx);
  const meta = tx?.attributes?.metadata || {};
  return (
    data.talkerNoShow === true ||
    data.endedReason === 'talker_no_show' ||
    meta.talkerNoShow === true ||
    meta.endedReason === 'talker_no_show'
  );
};

/**
 * Charge wording after cancel / no-show. Hold-release is not a refund.
 *
 * @param {Object} [tx]
 * @returns {'refunded'|'not_charged'}
 */
export const transactionChargeOutcome = tx => {
  const data = protectedDataOf(tx);
  const raw = data.chargeOutcome || data.refundStatus;
  if (raw === 'refunded' || raw === 'stripe_refunded') {
    return 'refunded';
  }
  if (raw === 'charged' || raw === 'captured') {
    return 'charged';
  }
  return 'not_charged';
};

const START_NOW_PAID_JOIN_TRANSITIONS = [
  'transition/confirm-payment',
  ...JOINABLE_LAST_TRANSITIONS,
];

/**
 * Paid Start now is Joinable after confirm-payment — Accept is automatic.
 *
 * @param {Object} tx
 * @returns {boolean}
 */
export const isStartNowPaidJoinTransition = tx => {
  if (!isStartNowBooking(null, tx)) {
    return false;
  }
  return START_NOW_PAID_JOIN_TRANSITIONS.includes(tx?.attributes?.lastTransition);
};

/**
 * Order/sale Join CTA. After a disconnect, show Rejoin while the slot is live.
 *
 * @param {string} [sessionFormat]
 * @param {{ rejoin?: boolean }} [options]
 * @returns {string}
 */
export const joinSessionCtaMessageId = (sessionFormat, { rejoin = false } = {}) => {
  if (rejoin) {
    return 'SessionPanel.rejoin';
  }
  if (sessionFormat === 'chat') {
    return 'SessionPanel.joinChatNow';
  }
  if (sessionFormat === 'video') {
    return 'SessionPanel.joinVideoCall';
  }
  if (sessionFormat === 'voice') {
    return 'SessionPanel.joinVoiceCall';
  }
  return 'SessionPanel.join';
};

/**
 * Leave copy matches the live format. Not destructive — Rejoin stays.
 *
 * @param {string} [sessionFormat]
 * @returns {string}
 */
export const leaveSessionCtaMessageId = sessionFormat =>
  sessionFormat === 'chat' ? 'SessionPanel.endChat' : 'SessionPanel.endCall';
// End chat and End call are red for both roles. Rejoin stays available.

/**
 * After the first enter, Rejoin stays offered for the rest of the live window.
 * Hide it only when the session has ended.
 *
 * @param {{ entered?: boolean, ended?: boolean, open?: boolean, handedOff?: boolean, failed?: boolean }} [state]
 * @returns {boolean}
 */
export const sessionNeedsRejoin = ({ entered = false, ended = false } = {}) => !!entered && !ended;

export const isInStartingSoonWindow = (startMs, endMs, nowMs = Date.now()) => {
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) {
    return false;
  }
  return nowMs >= startMs - STARTING_SOON_MS && nowMs <= endMs;
};

const SESSION_LENGTH_MS = 15 * 60 * 1000;
export const START_NOW_JOIN_GRACE_MS = 2 * 60 * 60 * 1000;

/**
 * Booked 15-minute slot end for Dashboard Sessions starting / Current.
 * Incoming Start now may carry a longer join-grace endsAt — Dashboard still
 * expires at the booked session length. Inbox Join grace is unchanged.
 *
 * @param {{ startsAt?: string, endsAt?: string }} [item]
 * @returns {number}
 */
export const notificationBookedEndMs = item => {
  const startMs = Date.parse(item?.startsAt);
  const endMs = Date.parse(item?.endsAt);
  const inferred = Number.isFinite(startMs) ? startMs + SESSION_LENGTH_MS : NaN;
  if (Number.isFinite(endMs) && Number.isFinite(inferred)) {
    return Math.min(endMs, inferred);
  }
  return Number.isFinite(inferred) ? inferred : endMs;
};

/**
 * Dashboard / banner Join CTA matches the booked format.
 *
 * @param {string} [sessionFormat]
 * @param {string} [namespace]
 * @returns {string}
 */
export const joinFormatCtaMessageId = (sessionFormat, _namespace = 'SessionStartBanner') =>
  joinSessionCtaMessageId(sessionFormat);

/**
 * Identity keys for one real booking. A paid order and an incoming Start now
 * for the same listing + start minute share a slot key even when IDs differ.
 *
 * @param {Object} [item]
 * @returns {string[]}
 */
export const notificationBookingKeys = item => {
  const keys = [];
  const joinId =
    typeof item?.joinParams?.id === 'string'
      ? item.joinParams.id
      : item?.joinName === 'OrderDetailsPage' || item?.joinName === 'SaleDetailsPage'
      ? item?.id
      : '';
  if (joinId) {
    keys.push(`tx:${joinId}`);
  }
  const listingId = typeof item?.listingId === 'string' ? item.listingId : '';
  const startMs = Date.parse(item?.startsAt);
  const startBucket = Number.isFinite(startMs) ? Math.floor(startMs / 60000) : '';
  if (listingId && startBucket !== '') {
    keys.push(`slot:${listingId}:${startBucket}`);
  }
  if (keys.length === 0 && item?.id) {
    keys.push(`id:${item?.kind || 'session'}:${item.id}`);
  }
  return keys;
};

export const notificationBookingKey = item => notificationBookingKeys(item)[0] || 'session:';

const notificationRank = item => {
  if (item?.joinName === 'OrderDetailsPage' || item?.joinName === 'SaleDetailsPage') {
    return 0;
  }
  if (item?.kind === 'starting_soon') {
    return 1;
  }
  return 2;
};

/**
 * True while the booked 15-minute window is still open.
 *
 * @param {Object} [item]
 * @param {number} [nowMs]
 * @returns {boolean}
 */
export const isJoinableDashboardNotification = (item, nowMs = Date.now()) => {
  if (!item?.id) {
    return false;
  }
  if (isTerminalJoinTransition(item.lastTransition)) {
    return false;
  }
  if (
    item.listenerNoShow ||
    item.endedReason === 'listener_no_show' ||
    item.talkerNoShow ||
    item.endedReason === 'talker_no_show'
  ) {
    return false;
  }
  const endMs = notificationBookedEndMs(item);
  return !Number.isFinite(endMs) || nowMs <= endMs;
};

/**
 * Real joinable Sessions starting items: still in the booked window, one
 * card per booking.
 *
 * @param {Array} [items]
 * @param {number} [nowMs]
 * @returns {Array}
 */
/**
 * Live session the person is not necessarily viewing: still joinable and
 * inside the booked window (or a Start now that has already been paid).
 *
 * @param {Object} [item]
 * @param {number} [nowMs]
 * @returns {boolean}
 */
export const isLiveActiveSessionNotification = (item, nowMs = Date.now()) => {
  if (!isJoinableDashboardNotification(item, nowMs)) {
    return false;
  }
  const endMs = notificationBookedEndMs(item);
  if (!Number.isFinite(endMs) || nowMs > endMs) {
    return false;
  }
  if (
    item?.kind === 'start_now' ||
    item?.startNow ||
    item?.preview === 'Join now' ||
    item?.preview === 'Join Session'
  ) {
    return true;
  }
  const startMs = Date.parse(item?.startsAt);
  return !Number.isFinite(startMs) || nowMs >= startMs;
};

export const uniqueJoinableSessionNotifications = (items = [], nowMs = Date.now()) => {
  const byKey = new Map();
  const chosen = [];
  (items || []).forEach(item => {
    if (!isJoinableDashboardNotification(item, nowMs)) {
      return;
    }
    const keys = notificationBookingKeys(item);
    const existing = keys.map(key => byKey.get(key)).find(Boolean);
    if (existing) {
      const winner = notificationRank(item) < notificationRank(existing) ? item : existing;
      if (winner !== existing) {
        notificationBookingKeys(existing).forEach(key => {
          if (byKey.get(key) === existing) {
            byKey.delete(key);
          }
        });
        const index = chosen.indexOf(existing);
        if (index >= 0) {
          chosen.splice(index, 1, winner);
        }
      }
      keys.forEach(key => byKey.set(key, winner));
      notificationBookingKeys(winner).forEach(key => byKey.set(key, winner));
      return;
    }
    chosen.push(item);
    keys.forEach(key => byKey.set(key, item));
  });
  return chosen;
};

/**
 * Sticky Continue chat / Join rows for an in-progress session.
 *
 * @param {Array} [items]
 * @param {number} [nowMs]
 * @returns {Array}
 */
export const liveActiveSessionNotifications = (items = [], nowMs = Date.now()) =>
  uniqueJoinableSessionNotifications(items, nowMs).filter(item =>
    isLiveActiveSessionNotification(item, nowMs)
  );

/**
 * Banner list for a Dashboard tab. Listening hides Start now here — those
 * land in Incoming now on Current.
 *
 * @param {Object} params
 * @param {Array} [params.items]
 * @param {boolean} [params.listeningTools]
 * @param {number} [params.nowMs]
 * @returns {Array}
 */
export const dashboardSessionBannerItems = ({
  items = [],
  listeningTools = false,
  transactions = [],
  nowMs = Date.now(),
} = {}) => {
  const unique = reconcileJoinableNotifications(items, { transactions, nowMs });
  return listeningTools ? unique.filter(item => item.kind !== 'start_now') : unique;
};

const acceptTransitionMs = tx => {
  const transitions = tx?.attributes?.transitions;
  if (Array.isArray(transitions)) {
    const hit = [...transitions]
      .reverse()
      .find(
        item =>
          item?.transition === 'transition/accept' ||
          item?.transition === 'transition/operator-accept'
      );
    const ms = Date.parse(hit?.createdAt);
    if (Number.isFinite(ms)) {
      return ms;
    }
  }
  const last = tx?.attributes?.lastTransition;
  if (last === 'transition/accept' || last === 'transition/operator-accept') {
    const ms = Date.parse(tx?.attributes?.lastTransitionedAt);
    if (Number.isFinite(ms)) {
      return ms;
    }
  }
  return NaN;
};

/**
 * Start now stays Joinable after a late Accept: later of booking end
 * or Accept/pay time, plus a two-hour grace.
 *
 * @param {Object} params
 * @returns {number}
 */
export const startNowJoinDeadlineMs = ({ endMs, acceptedAtMs, lastTransitionedAtMs } = {}) => {
  const anchors = [endMs, acceptedAtMs, lastTransitionedAtMs].filter(Number.isFinite);
  if (anchors.length === 0) {
    return NaN;
  }
  return Math.max(...anchors) + START_NOW_JOIN_GRACE_MS;
};

/**
 * Classify a Join click against the booking clock. Same 10-minute
 * early window as Join-soon notify. Start now stays ready through a
 * two-hour grace after the later of booking end or Accept.
 *
 * @param {Object} params
 * @param {boolean} [params.startNow]
 * @param {number} [params.startMs]
 * @param {number} [params.endMs]
 * @param {number} [params.acceptedAtMs]
 * @param {number} [params.lastTransitionedAtMs]
 * @param {number} params.nowMs
 * @returns {{ status: 'ready'|'too_early'|'ended', startMs: number, endMs: number }}
 */
export const joinSessionWindowState = ({
  startNow = false,
  startMs,
  endMs,
  acceptedAtMs,
  lastTransitionedAtMs,
  nowMs,
}) => {
  const bookedEnd = Number.isFinite(endMs)
    ? endMs
    : Number.isFinite(startMs)
    ? startMs + SESSION_LENGTH_MS
    : NaN;
  const end = startNow
    ? startNowJoinDeadlineMs({
        endMs: bookedEnd,
        acceptedAtMs,
        lastTransitionedAtMs,
      })
    : bookedEnd;
  if (Number.isFinite(end) && nowMs > end) {
    return { status: 'ended', startMs, endMs: end };
  }
  if (startNow) {
    return { status: 'ready', startMs, endMs: end };
  }
  const resolvedEnd = Number.isFinite(endMs) ? endMs : bookedEnd;
  if (isInStartingSoonWindow(startMs, resolvedEnd, nowMs)) {
    return { status: 'ready', startMs, endMs: resolvedEnd };
  }
  if (Number.isFinite(startMs)) {
    return { status: 'too_early', startMs, endMs: resolvedEnd };
  }
  // Scheduled bookings with no clock are not Join-ready. Start now / demo
  // already returned ready above. Missing times used to default to ready and
  // painted "Join chat now" on upcoming Book-a-time pages.
  return { status: 'too_early', startMs, endMs: resolvedEnd };
};

/**
 * Paid Start now is Join-ready on Accept. Scheduled slots wait until
 * a few minutes before start (and stay ready until the slot ends).
 *
 * @param {Object} params
 * @returns {boolean}
 */
export const isJoinReadySession = params => joinSessionWindowState(params).status === 'ready';

/**
 * Dashboard / Inbox copy before the 5-minute Join window.
 *
 * @param {number} startMs
 * @param {number} [nowMs]
 * @returns {{ id: string, values: Object }}
 */
export const scheduledStartCopy = (startMs, nowMs = Date.now()) => {
  if (!Number.isFinite(startMs)) {
    return { id: 'DashboardPage.startsSoonLabel', values: {} };
  }
  const remaining = startMs - nowMs;
  if (remaining > 0 && remaining <= 60 * 60 * 1000) {
    const minutes = Math.max(1, Math.ceil(remaining / 60000));
    return { id: 'DashboardPage.startsInMinutes', values: { minutes } };
  }
  const start = new Date(startMs);
  const time = start.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'America/New_York',
  });
  return { id: 'DashboardPage.startsAtTime', values: { time } };
};

const bookingBounds = tx => {
  const { start, end } = bookingTimes(tx);
  return { startMs: start?.getTime() ?? NaN, endMs: end?.getTime() ?? NaN };
};

export const startNowJoinDeadlineFromTransaction = tx => {
  const { endMs } = bookingBounds(tx);
  return startNowJoinDeadlineMs({
    endMs,
    acceptedAtMs: acceptTransitionMs(tx),
    lastTransitionedAtMs: Date.parse(tx?.attributes?.lastTransitionedAt),
  });
};

const startNowWindowParams = tx => ({
  startNow: isStartNowBooking(null, tx),
  ...bookingBounds(tx),
  acceptedAtMs: acceptTransitionMs(tx),
  lastTransitionedAtMs: Date.parse(tx?.attributes?.lastTransitionedAt),
});

/**
 * Booking clock + Start now flag for Join gating.
 *
 * @param {Object} tx
 * @returns {{ startNow: boolean, startMs: number, endMs: number }}
 */
export const bookingWindowFromTransaction = tx => ({
  startNow: isStartNowBooking(null, tx),
  ...bookingBounds(tx),
});

/**
 * Inbox row Join CTA — same window as the order/sale page.
 *
 * @param {Object} tx
 * @param {number} [nowMs]
 * @returns {boolean}
 */
export const isInboxJoinReady = (tx, nowMs = Date.now()) => {
  return isStillJoinableTransaction(tx, nowMs);
};

const transactionIdOf = value =>
  value?.id?.uuid || (typeof value?.id === 'string' ? value.id : null);

/**
 * Live Sharetribe state still allows Join (accepted / paid Start now, in window).
 *
 * @param {Object} [tx]
 * @param {number} [nowMs]
 * @returns {boolean}
 */
export const isStillJoinableTransaction = (tx, nowMs = Date.now()) => {
  if (
    !tx ||
    transactionIsListenerNoShow(tx) ||
    transactionIsTalkerNoShow(tx) ||
    transactionHidesJoin(tx) ||
    isSupersededEditedBooking(tx) ||
    txPastNoShowDeadline(tx, new Date(nowMs))
  ) {
    return false;
  }
  const last = tx?.attributes?.lastTransition;
  if (isTerminalJoinTransition(last)) {
    return false;
  }
  if (!isJoinableLastTransition(last) && !isStartNowPaidJoinTransition(tx)) {
    return false;
  }
  return isJoinReadySession({
    ...startNowWindowParams(tx),
    nowMs,
  });
};

/**
 * Drop Session starting / Join rows that the live transaction can no longer support.
 *
 * @param {Array} [items]
 * @param {Object} [options]
 * @param {Array|Object} [options.transactions]
 * @param {number} [options.nowMs]
 * @returns {Array}
 */
export const reconcileJoinableNotifications = (
  items = [],
  { transactions = [], nowMs = Date.now() } = {}
) => {
  const list = Array.isArray(transactions) ? transactions : Object.values(transactions || {});
  const byId = list.reduce((acc, tx) => {
    const id = transactionIdOf(tx);
    if (id) {
      acc[id] = tx;
    }
    return acc;
  }, {});
  return uniqueJoinableSessionNotifications(items, nowMs).filter(item => {
    const txId = item?.joinParams?.id || item?.id;
    const tx = byId[txId];
    if (!tx) {
      return true;
    }
    return isStillJoinableTransaction(tx, nowMs);
  });
};

/**
 * Show a Zoom-style Join on accepted talk sessions until the slot ends.
 * Missing booking times still offer Join for default-booking. Ended slots do not.
 *
 * @param {Object} tx
 * @param {number} [nowMs]
 * @returns {boolean}
 */
export const isInboxJoinOffered = (tx, nowMs = Date.now()) => {
  if (
    transactionIsListenerNoShow(tx) ||
    transactionIsTalkerNoShow(tx) ||
    transactionHidesJoin(tx) ||
    isSupersededEditedBooking(tx) ||
    txPastNoShowDeadline(tx, new Date(nowMs))
  ) {
    return false;
  }
  const last = tx?.attributes?.lastTransition;
  if (isTerminalJoinTransition(last)) {
    return false;
  }
  if (!isJoinableLastTransition(last) && !isStartNowPaidJoinTransition(tx)) {
    return false;
  }
  const processName = tx?.attributes?.processName;
  if (processName && processName !== 'default-booking') {
    return false;
  }
  const window = joinSessionWindowState({
    ...startNowWindowParams(tx),
    nowMs,
  });
  return window.status !== 'ended';
};

/**
 * Inbox Join label: Join now when the slot is live, otherwise assigned-time copy.
 *
 * @param {Object} tx
 * @param {number} [nowMs]
 * @returns {string}
 */
export const inboxJoinCtaMessageId = (tx, nowMs = Date.now()) => {
  if (isInboxJoinReady(tx, nowMs)) {
    const txId = tx?.id?.uuid || tx?.id;
    return joinSessionCtaMessageId(sessionFormatFromTransaction(tx), {
      rejoin: hasRememberedSessionEntry(txId),
    });
  }
  return 'InboxPage.joinAtAssignedTime';
};

export const safeDisplayName = name => {
  const raw = typeof name === 'string' ? name.trim() : '';
  if (!raw || PHONE_RE.test(raw) || EMAIL_RE.test(raw)) {
    return 'Someone';
  }
  return raw.slice(0, 80);
};

export const safeListingTitle = title => {
  const raw = typeof title === 'string' ? title.trim() : '';
  if (!raw || PHONE_RE.test(raw) || EMAIL_RE.test(raw)) {
    return 'Talk session';
  }
  return raw.slice(0, 120);
};

export const notificationTag = item => `${item?.kind || 'session'}:${item?.id || ''}`;

export const notificationCopy = item => {
  const who = safeDisplayName(item?.otherDisplayName);
  const format = formatLabel(item?.sessionFormat);
  const join = joinPhrase(item?.sessionFormat);
  if (item?.kind === 'start_now') {
    return {
      title: `Click here to ${join} now`,
      body: `${who} started a 15-min ${format}. Your assigned time is now.`,
    };
  }
  return {
    title: `Click here to ${join} at your assigned time`,
    body: `15-min ${format} with ${who}. Open the link to join.`,
  };
};

/**
 * In-app Join banner for the order/sale page after Start now pay.
 * Talker and listener both get Join now without waiting on Dashboard.
 *
 * @param {Object} [params]
 * @param {Object} [params.transaction]
 * @param {string} [params.transactionRole]
 * @returns {Object|null}
 */
export const startNowJoinBannerItem = ({ transaction, transactionRole } = {}) => {
  if (!isStartNowPaidJoinTransition(transaction)) {
    return null;
  }
  const id = transaction?.id?.uuid || (typeof transaction?.id === 'string' ? transaction.id : null);
  if (!id) {
    return null;
  }
  const isCustomer = transactionRole === 'customer';
  const other = isCustomer ? transaction?.provider : transaction?.customer;
  return sanitizeNotification({
    id: `join_ready:${id}`,
    kind: 'start_now',
    listingId: transaction?.listing?.id?.uuid,
    listingTitle: transaction?.listing?.attributes?.title,
    sessionFormat: sessionFormatFromTransaction(transaction),
    role: isCustomer ? 'customer' : 'provider',
    otherDisplayName: isCustomer
      ? transaction?.listing?.attributes?.title || other?.attributes?.profile?.displayName
      : other?.attributes?.profile?.displayName,
    joinName: isCustomer ? 'OrderDetailsPage' : 'SaleDetailsPage',
    joinParams: { id },
  });
};

export const sanitizeNotification = raw => {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const item = {};
  NOTIFICATION_PUBLIC_KEYS.forEach(key => {
    if (raw[key] !== undefined) {
      item[key] = raw[key];
    }
  });
  if (item.otherDisplayName) {
    item.otherDisplayName = safeDisplayName(item.otherDisplayName);
  }
  if (item.listingTitle) {
    item.listingTitle = safeListingTitle(item.listingTitle);
  }
  if (item.joinParams && typeof item.joinParams === 'object') {
    const id = typeof item.joinParams.id === 'string' ? item.joinParams.id : undefined;
    item.joinParams = id ? { id } : {};
  }
  if (!item.kind || !item.id) {
    return null;
  }
  return item;
};

const collectKeys = (value, keys = []) => {
  if (!value || typeof value !== 'object') {
    return keys;
  }
  if (Array.isArray(value)) {
    value.forEach(item => collectKeys(item, keys));
    return keys;
  }
  Object.keys(value).forEach(key => {
    keys.push(key);
    collectKeys(value[key], keys);
  });
  return keys;
};

export const payloadLooksSafe = value => {
  const keys = collectKeys(value);
  if (keys.some(key => FORBIDDEN_KEY_RE.test(key))) {
    return false;
  }
  const names = [value?.otherDisplayName, value?.listingTitle].filter(Boolean).join(' ');
  return !PHONE_RE.test(names) && !EMAIL_RE.test(names);
};

export const maybeShowBrowserNotifications = items => {
  if (typeof window === 'undefined' || typeof Notification === 'undefined') {
    return;
  }
  if (Notification.permission !== 'granted') {
    return;
  }
  (items || []).forEach(item => {
    const sanitized = sanitizeNotification(item);
    if (!sanitized) {
      return;
    }
    const tag = notificationTag(sanitized);
    const key = `${BROWSER_NOTIF_STORAGE_PREFIX}${tag}`;
    try {
      if (window.sessionStorage?.getItem(key)) {
        return;
      }
      window.sessionStorage?.setItem(key, '1');
    } catch (e) {
      // Private mode can throw; still try to show once.
    }
    const copy = notificationCopy(sanitized);
    try {
      const n = new Notification(copy.title, { body: copy.body, tag });
      n.onclick = () => {
        window.focus();
        const path =
          sanitized.joinName === 'OrderDetailsPage' && sanitized.joinParams?.id
            ? `/order/${sanitized.joinParams.id}?${JOIN_SESSION_ENTER_PARAM}=1#${JOIN_SESSION_HASH}`
            : sanitized.joinName === 'SaleDetailsPage' && sanitized.joinParams?.id
            ? `/sale/${sanitized.joinParams.id}?${JOIN_SESSION_ENTER_PARAM}=1#${JOIN_SESSION_HASH}`
            : '/dashboard';
        window.location.assign(path);
      };
    } catch (e) {
      // Ignore unsupported Notification constructors.
    }
  });
};

export const requestBrowserNotificationPermission = () => {
  if (typeof window === 'undefined' || typeof Notification === 'undefined') {
    return Promise.resolve('denied');
  }
  return Notification.requestPermission();
};

export const browserNotificationsSupported = () =>
  typeof window !== 'undefined' && typeof Notification !== 'undefined';
